import { ReactNode } from "react";
import { Accordion, AccordionDetails, AccordionSummary, Chip, DialogContent, List, ListItem, ListItemText, Stack, Typography } from "@mui/material";
import { ExpandMore, FactCheckOutlined } from "@mui/icons-material";
import { MoleCancelButton, MoleDialog, MoleDialogActions, MoleDialogHeader, useMoleThemeColor } from "../../components/MoleDialogShell";
import { useUI } from "../../providers/MoleUIContext";
import { Util } from "../../util/Util";
import type { InventoryImportSummary as Summary } from "./inventorySheet";

interface Props {
	open: boolean;
	onClose: () => void;
	summary: Summary | null;
	fileName?: string;
}

const Section = ({ title, count, color, children }: { title: string; count: number; color: "success" | "info" | "warning" | "error" | "default"; children?: ReactNode }) => (
	<Accordion disableGutters defaultExpanded={count > 0 && color !== "success" && color !== "info"} disabled={count === 0 || !children}>
		<AccordionSummary expandIcon={children ? <ExpandMore /> : undefined}>
			<Stack direction="row" spacing={1} alignItems="center">
				<Chip size="small" label={count} color={color} />
				<Typography variant="body2" fontWeight={600}>{title}</Typography>
			</Stack>
		</AccordionSummary>
		{children && <AccordionDetails sx={{ pt: 0 }}>{children}</AccordionDetails>}
	</Accordion>
);

const Names = ({ items }: { items: ReactNode[] }) => (
	<List dense disablePadding sx={{ maxHeight: 220, overflow: "auto" }}>
		{items.map((item, index) => (
			<ListItem key={index} disableGutters>
				<ListItemText primary={item} />
			</ListItem>
		))}
	</List>
);

const InventoryImportSummary = ({ open, onClose, summary, fileName }: Props) => {
	const { t } = useUI();
	const themeColor = useMoleThemeColor();
	if (!summary) return null;

	const k = (key: string, options?: Record<string, unknown>) => t(`common:inventoryModal.sheet.summary.${key}`, options);

	return (
		<MoleDialog open={open} onClose={onClose} maxWidth="sm" fullWidth themeColor={themeColor}>
			<MoleDialogHeader
				title={k("title")}
				subtitle={fileName}
				icon={<FactCheckOutlined />}
				onClose={onClose}
				themeColor={themeColor}
			/>
			<DialogContent sx={{ pt: 2 }}>
				<Section title={k("updated")} count={summary.updated.length} color="success">
					<Names items={summary.updated} />
				</Section>
				<Section title={k("added")} count={summary.added.length} color="info">
					<Names items={summary.added} />
				</Section>
				<Section title={k("blank")} count={summary.blank} color="default" />
				<Section title={k("drifted")} count={summary.drifted.length} color="warning">
					<Names
						items={summary.drifted.map((entry) =>
							k("driftedLine", {
								name: entry.name,
								fileStock: Util.formatNumber(entry.fileStock),
								currentStock: Util.formatNumber(entry.currentStock),
							}),
						)}
					/>
				</Section>
				<Section title={k("invalid")} count={summary.invalid.length} color="error">
					<Names
						items={summary.invalid.map((entry) =>
							k("invalidLine", { row: entry.row, name: entry.name, reason: k(`reason.${entry.reason}`) }),
						)}
					/>
				</Section>
				<Section title={k("unknown")} count={summary.unknown.length} color="error">
					<Names items={summary.unknown.map((entry) => k("unknownLine", { row: entry.row, name: entry.name || `#${entry.id}` }))} />
				</Section>
				<Section title={k("duplicates")} count={summary.duplicates.length} color="warning">
					<Names items={summary.duplicates} />
				</Section>
				<Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 2 }}>
					{k("notPosted")}
				</Typography>
			</DialogContent>
			<MoleDialogActions>
				<MoleCancelButton onClick={onClose}>{t("common:ui.buttons.close")}</MoleCancelButton>
			</MoleDialogActions>
		</MoleDialog>
	);
};

export default InventoryImportSummary;
